import { useEffect, useState } from "react";
import { useConvex } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api, type Id } from "./api";
import { collectSearchPages } from "../domain/completeSearch";
import { caughtErrorMessage } from "../domain/errors";
import type { Jurisdiction, LegislationKind } from "../../convex/model/enums";

export type SourceSearchMatch = FunctionReturnType<typeof api.workflows.searchPage>["page"][number];
export function useCompleteSourceSearch(companyId: Id<"companies"> | null, query: string, jurisdiction?: Jurisdiction, kind?: LegislationKind) {
  const client = useConvex();
  const key = JSON.stringify([companyId, query, jurisdiction, kind]);
  const [state, setState] = useState<{ key: string; matches?: SourceSearchMatch[]; found: number; error?: string }>({ key, found: 0 });
  useEffect(() => {
    let cancelled = false;
    if (!companyId || query.length < 2) return;
    const timer = setTimeout(() => {
      setState({ key, found: 0 });
      void collectSearchPages(cursor => client.query(api.workflows.searchPage, { companyId, query, jurisdiction, kind, paginationOpts: { cursor, numItems: 100 } }), () => cancelled,
        found => { if (!cancelled) setState({ key, found }); })
        .then(matches => { if (matches && !cancelled) setState({ key, found: matches.length, matches }); })
        .catch(error => { if (!cancelled) setState({ key, found: 0, error: caughtErrorMessage(error, "Search could not complete") }); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [client, companyId, query, jurisdiction, kind, key]);
  return state.key === key ? state : { key, found: 0 };
}
